import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  History,
  Eye,
  Heart,
  MessageSquare,
  DollarSign,
  Building2,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface HistoryItem {
  id: string;
  type: "view" | "like" | "message" | "offer" | "status" | "transaction";
  action: string;
  description: string;
  timestamp: string;
  propertyId?: string;
  propertyTitle?: string;
  status?: "success" | "pending" | "failed";
  amount?: number;
}

export function HistoryPage() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<string>("all");
  const [period, setPeriod] = useState<string>("all");

  // Ejemplo de historial (reemplazar con datos reales)
  const historyItems: HistoryItem[] = [
    {
      id: "1",
      type: "transaction",
      action: "Pago de publicación",
      description: "Pago por publicación destacada de 'Apartamento en Altamira'",
      timestamp: "2024-02-01T14:30:00",
      propertyId: "prop1",
      propertyTitle: "Apartamento en Altamira",
      status: "success",
      amount: 49.99
    },
    {
      id: "2",
      type: "status",
      action: "Propiedad publicada",
      description: "Tu propiedad 'Casa en El Hatillo' ha sido publicada exitosamente",
      timestamp: "2024-02-01T12:15:00",
      propertyId: "prop2",
      propertyTitle: "Casa en El Hatillo",
      status: "success"
    },
    {
      id: "3",
      type: "offer",
      action: "Oferta enviada",
      description: "Has enviado una oferta por la propiedad 'Local en Las Mercedes'",
      timestamp: "2024-02-01T10:45:00",
      propertyId: "prop3",
      propertyTitle: "Local en Las Mercedes",
      status: "pending",
      amount: 150000
    },
    {
      id: "4",
      type: "message",
      action: "Mensaje enviado",
      description: "Has enviado un mensaje al propietario de 'Apartamento en Altamira'",
      timestamp: "2024-02-01T09:30:00",
      propertyId: "prop1",
      propertyTitle: "Apartamento en Altamira"
    },
    {
      id: "5",
      type: "like",
      action: "Propiedad guardada",
      description: "Has guardado 'Casa en El Hatillo' en tus favoritos",
      timestamp: "2024-01-31T16:20:00",
      propertyId: "prop2",
      propertyTitle: "Casa en El Hatillo"
    }
  ];

  const getActionIcon = (type: string) => {
    switch (type) {
      case "view":
        return <Eye className="h-4 w-4" />;
      case "like":
        return <Heart className="h-4 w-4" />;
      case "message":
        return <MessageSquare className="h-4 w-4" />;
      case "offer":
        return <DollarSign className="h-4 w-4" />;
      case "status":
        return <CheckCircle2 className="h-4 w-4" />;
      case "transaction":
        return <DollarSign className="h-4 w-4" />;
      default:
        return <History className="h-4 w-4" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "success":
        return "Completado";
      case "pending":
        return "Pendiente";
      case "failed":
        return "Fallido";
      default:
        return status;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredHistory = historyItems.filter(item => {
    if (filter !== "all" && item.type !== filter) return false;
    
    if (period !== "all") {
      const date = new Date(item.timestamp);
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      
      switch (period) {
        case "today":
          return days === 0;
        case "week":
          return days <= 7;
        case "month":
          return days <= 30;
        default:
          return true;
      }
    }
    
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Historial</h2>
          <p className="text-muted-foreground">
            Registro de todas tus actividades y transacciones
          </p>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Actividades
              </CardTitle>
              <History className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{historyItems.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Transacciones
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {historyItems.filter(item => item.type === "transaction").length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Ofertas
              </CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {historyItems.filter(item => item.type === "offer").length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Mensajes
              </CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {historyItems.filter(item => item.type === "message").length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4 justify-end">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Periodo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo el tiempo</SelectItem>
              <SelectItem value="today">Hoy</SelectItem>
              <SelectItem value="week">Última semana</SelectItem>
              <SelectItem value="month">Último mes</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Tipo de actividad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="transaction">Transacciones</SelectItem>
              <SelectItem value="offer">Ofertas</SelectItem>
              <SelectItem value="message">Mensajes</SelectItem>
              <SelectItem value="status">Estados</SelectItem>
              <SelectItem value="like">Me gusta</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* History Table */}
        <Card>
          <CardHeader>
            <CardTitle>Actividades Recientes</CardTitle>
            <CardDescription>
              {filteredHistory.length} actividades encontradas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Actividad</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Propiedad</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHistory.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatTimestamp(item.timestamp)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center",
                          "bg-primary/10"
                        )}>
                          {getActionIcon(item.type)}
                        </div>
                        <span>{item.action}</span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[300px]">
                      <p className="truncate">{item.description}</p>
                    </TableCell>
                    <TableCell>
                      {item.propertyTitle && (
                        <div className="flex items-center gap-1">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate">{item.propertyTitle}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.status && (
                        <div className="flex items-center gap-2">
                          {getStatusIcon(item.status)}
                          <span>{getStatusText(item.status)}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.amount && (
                        <span className="font-medium">
                          ${item.amount.toLocaleString()}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
} 